import React, { FC, useMemo } from 'react';
import { FaArrowCircleLeft, FaArrowCircleRight } from 'react-icons/fa';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  ArrowIconContainer,
  Box,
  BoxContainer,
  BoxesContainer,
  DescriptionContainer,
  DicomCardContainer,
  DicomCardsContainer,
  DicomsContainer,
  DicomThumbnail,
  DicomVideoThumbnail,
  EmptyBox,
  ViewPredictionTag,
} from './Dicoms.style';
import { IDicom, IDicomViewPrediction } from 'models/icardio';
import { useReviewMode } from 'services/providers';
import { getDicomMediaType } from 'utilities/mediaType';
import { formatPipelineLabel } from '../FilterSelector';

interface DicomsProps {
  dicoms: IDicom[];
  checkIfDicomIsLabeled: (dicom: any) => any;
  changesAreSaved: boolean;
  dicomSideBarIsOpen: boolean;
  setDicomSideBarIsOpen: (open: boolean) => void;
  dicomViewPredictions?: IDicomViewPrediction[];
  selectedPipelines?: string[];
  rejectedDicomUuids?: string[];
}

const NO_PIPELINES: string[] = [];
const NO_DICOM_UUIDS: string[] = [];

interface IDicomViewTag {
  label: string;
  tooltip: string;
}

const formatPrediction = (prediction: IDicomViewPrediction) =>
  `${prediction.window} - ${prediction.view} | ${formatPipelineLabel(
    prediction.pipelineName,
    prediction.pipelineId
  )} | ${Math.round(prediction.confidence * 100)}%`;

// One tag per dicom: top-confidence prediction, preferring the pipelines selected in the filter
const buildDicomViewTags = (
  predictions: IDicomViewPrediction[],
  selectedPipelines: string[]
): Map<string, IDicomViewTag> => {
  const selected = new Set(selectedPipelines);
  const byDicom = new Map<string, IDicomViewPrediction[]>();
  predictions.forEach(prediction => {
    const dicomPredictions = byDicom.get(prediction.dicom);
    if (dicomPredictions) dicomPredictions.push(prediction);
    else byDicom.set(prediction.dicom, [prediction]);
  });

  const tags = new Map<string, IDicomViewTag>();
  byDicom.forEach((dicomPredictions, dicomUuid) => {
    const sorted = [...dicomPredictions].sort((a, b) => {
      const aSelected = selected.has(a.pipelineId) ? 1 : 0;
      const bSelected = selected.has(b.pipelineId) ? 1 : 0;
      return bSelected - aSelected || b.confidence - a.confidence;
    });
    tags.set(dicomUuid, {
      label: sorted[0].view ?? '',
      tooltip: sorted.map(formatPrediction).join('\n'),
    });
  });
  return tags;
};

const Dicoms: FC<DicomsProps> = ({
  dicoms,
  checkIfDicomIsLabeled,
  changesAreSaved,
  dicomSideBarIsOpen,
  setDicomSideBarIsOpen,
  dicomViewPredictions,
  selectedPipelines = NO_PIPELINES,
  rejectedDicomUuids = NO_DICOM_UUIDS,
}) => {
  const { studyIcid, dicomIcid } = useParams();
  const navigate = useNavigate();
  const { isReviewMode } = useReviewMode();

  const dicomViewTags = useMemo(
    () => buildDicomViewTags(dicomViewPredictions ?? [], selectedPipelines),
    [dicomViewPredictions, selectedPipelines]
  );
  const rejectedDicoms = useMemo(() => new Set(rejectedDicomUuids), [rejectedDicomUuids]);

  const dicomCards = dicoms?.map(dicom => {
    const isActive = dicom?.icid === dicomIcid;
    const dicomLink = `/${studyIcid}/${dicom?.icid}${isReviewMode ? '?isReviewMode=true' : ''}`;
    const isVideo = getDicomMediaType(dicom?.mediaType, dicom?.numberOfFrames) === 'Video';

    const {
      viewLabelIsLabeled,
      phaseLabelsAreLabeled,
      keypointCollectionLabelsAreLabeled,
      segmentationsAreLabeled,
      phaseLabelsAreRequired,
      keypointCollectionLabelsAreRequired,
      segmentationsAreRequired,
      dicomIsLabeled,
    } = checkIfDicomIsLabeled(dicom);

    const handleDicomOnClick = event => {
      event.preventDefault();
      if (changesAreSaved || window.confirm('You have unsaved changes on this dicom, continue?'))
        navigate(dicomLink);
    };

    const viewTag = dicomViewTags.get(dicom.uuid);

    const emptyBox = <EmptyBox>-</EmptyBox>;
    const nonRequiredBox = <EmptyBox>x</EmptyBox>;

    return (
      <DicomCardContainer
        key={dicom?.index}
        isActive={isActive}
        onClick={handleDicomOnClick}
        isLabeled={dicomIsLabeled}
        $isReviewRejected={rejectedDicoms.has(dicom.uuid)}
      >
        <Link to={dicomLink} onClick={e => e.preventDefault()}>
          {isVideo ? (
            <DicomVideoThumbnail src={dicom?.file} />
          ) : (
            <DicomThumbnail src={dicom?.file} alt="image-thumbnail" />
          )}
          <DescriptionContainer isHidden={!dicomSideBarIsOpen}>
            <div>
              [{String(dicom?.index + 1).padStart(2, '0')}] {dicom?.icid}
            </div>
            <BoxesContainer>
              <BoxContainer>
                {viewLabelIsLabeled ? <Box type="identifier" /> : emptyBox}
                {phaseLabelsAreLabeled ? (
                  <Box type="phaser" />
                ) : phaseLabelsAreRequired ? (
                  emptyBox
                ) : (
                  nonRequiredBox
                )}
                {keypointCollectionLabelsAreLabeled ? (
                  <Box type="keypointer" />
                ) : keypointCollectionLabelsAreRequired ? (
                  emptyBox
                ) : (
                  nonRequiredBox
                )}
                {segmentationsAreLabeled ? (
                  <Box type="segmentor" />
                ) : segmentationsAreRequired ? (
                  emptyBox
                ) : (
                  nonRequiredBox
                )}
              </BoxContainer>
              <BoxContainer>
                {viewTag && (
                  <ViewPredictionTag title={viewTag.tooltip}>{viewTag.label}</ViewPredictionTag>
                )}
                {dicomIsLabeled && <Box type="labeled" />}
              </BoxContainer>
            </BoxesContainer>
          </DescriptionContainer>
        </Link>
      </DicomCardContainer>
    );
  });

  const handleCollapseDicomSideBar = () => setDicomSideBarIsOpen(!dicomSideBarIsOpen);

  return (
    <DicomsContainer>
      <ArrowIconContainer onClick={handleCollapseDicomSideBar}>
        {dicomSideBarIsOpen ? <FaArrowCircleLeft size={50} /> : <FaArrowCircleRight size={50} />}
      </ArrowIconContainer>
      <DicomCardsContainer>{dicomCards}</DicomCardsContainer>
    </DicomsContainer>
  );
};

export default Dicoms;
